import { Link } from 'react-router-dom';

import image1 from '../../../../assets/images/work/norcat-mine-experience/NORCATMineExperience_detailed_img_1.webp';
import image2 from '../../../../assets/images/work/norcat-mine-experience/NORCATMineExperience_detailed_img_2.webp';
import image3 from '../../../../assets/images/work/norcat-mine-experience/NORCATMineExperience_detailed_abovefooter.webp';
import artwork from '../../../../assets/images/art_2.png';
import eoLogoLow from '../../../../assets/images/eologo-low.webp';
import eoLogo from '../../../../assets/images/eologo.webp';

const Data_NORCATUndergroundExperience = {
    content: (
        <>
            <div className="text-content">
                <div className="text-content-wrapper position-relative bg-black">
                    <div className="text-content-inner">
                        <div className="container">
                            <div className="row">
                                <div className="col-lg-6">
                                    <h4 className="heading heading-small light-2">PROBLEM</h4>
                                    <div className="paragraph light-1">
                                        <p>
                                            An underground mine is a complex environment that can be difficult to
                                            understand before experiencing it firsthand. New learners need more than
                                            isolated lessons—they need context for how safety procedures, people,
                                            equipment, and processes work together throughout a developing mine.
                                        </p>
                                        <br />
                                        <p>
                                            NORCAT wanted to give learners a realistic understanding of the full
                                            underground mining experience before they arrived on site for hands-on
                                            training with real people and equipment.
                                        </p>
                                    </div>
                                </div>

                                <div className="col-lg-6 second-column">
                                    <h4 className="heading heading-small light-2">CHALLENGES</h4>
                                    <div className="paragraph light-1">
                                        <p>
                                            The experience needed to recreate the conditions and complexity of an
                                            underground operation while keeping a large amount of information
                                            approachable for new learners.
                                        </p>
                                        <br />
                                        <p>
                                            It also needed to connect foundational safety expectations with the complete
                                            mining cycle, showing how specialized roles, infrastructure, and equipment
                                            contribute to a safe and productive operation.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="project-content">
                <div className="project-content-wrapper">
                    <div className="container">
                        <div className="row">
                            <div className="col-lg-8">
                                <div className="project-content-inner">
                                    <h2 className="heading heading-large dark-1">
                                        A guided journey through a developing underground mine—before learners ever
                                        arrive on site.
                                    </h2>

                                    <div className="paragraph dark-1">
                                        <p>
                                            The NORCAT Underground Experience takes learners through a digital
                                            recreation of the NORCAT Underground Centre. Travelling in a side-by-side
                                            utility vehicle, they explore the mine and see how each stage of an
                                            underground operation connects to the next.
                                        </p>
                                        <br />
                                        <p>
                                            By experiencing the environment digitally, learners can become familiar with
                                            underground conditions, terminology, processes, equipment, and safety
                                            expectations before moving into onsite training.
                                        </p>
                                    </div>

                                    <div className="heading heading-medium dark-1">
                                        <p>Safety Starts Before the Descent</p>
                                    </div>

                                    <div className="paragraph dark-1">
                                        <p>
                                            The journey begins with the fundamentals of underground safety. Learners are
                                            introduced to the practices and systems that shape every shift before
                                            travelling deeper into the mine.
                                        </p>
                                    </div>

                                    <div className="text-list medium dark-1">
                                        <ul>
                                            <li>PPE requirements and pre-entry preparation</li>
                                            <li>Tagging in and out</li>
                                            <li>Emergency procedures and refuge stations</li>
                                            <li>Ventilation and underground air quality</li>
                                            <li>Traffic rules and right-of-way</li>
                                            <li>Safe interaction with underground equipment</li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="single-image-wrapper">
                        <div className="single-image">
                            <img
                                src={image1}
                                alt="Learner view inside the NORCAT Underground Experience"
                                loading="lazy"
                                decoding="async"
                            />
                        </div>
                        <div className="single-image">
                            <img
                                src={image2}
                                alt="Digital recreation of the NORCAT Underground Centre"
                                loading="lazy"
                                decoding="async"
                            />
                        </div>
                    </div>

                    <div className="container">
                        <div className="row">
                            <div className="col-lg-8">
                                <div className="project-content-inner">
                                    <h2 className="heading heading-medium dark-1">
                                        Connecting the Complete Mining Cycle
                                    </h2>

                                    <div className="paragraph dark-1">
                                        <p>
                                            As learners travel through the mine, they discover the stages, systems, and
                                            skilled people that keep an underground operation moving. The experience
                                            introduces surveying and mine planning, drilling, loading and blasting,
                                            ventilation, scaling, mucking, ground support, electrical infrastructure,
                                            and maintenance.
                                        </p>
                                        <br />
                                        <p>
                                            Presenting these elements as one connected journey helps learners understand
                                            the complete mining cycle rather than seeing each task in isolation. It also
                                            exposes them to the wide range of skilled careers that support work
                                            underground.
                                        </p>
                                    </div>

                                    <div className="heading heading-medium dark-1">
                                        <p>A Foundation Designed to Grow</p>
                                    </div>

                                    <div className="paragraph dark-1">
                                        <p>
                                            This experience represents the first phase of the program. Its digital mine
                                            environment creates a foundation for additional modules and learning
                                            experiences as the platform continues to grow.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="single-image">
                        <img
                            src={image3}
                            alt="Underground equipment and infrastructure represented in the NORCAT Underground Experience"
                            loading="lazy"
                            decoding="async"
                        />
                    </div>
                </div>
            </div>

            <div className="text-content">
                <div className="text-content-wrapper position-relative bg-black">
                    <div className="text-content-inner">
                        <div className="container">
                            <div className="row">
                                <div className="col-lg-6">
                                    <h4 className="heading heading-small light-2">RESULTS</h4>
                                    <div className="paragraph light-1">
                                        <p>
                                            The first phase gives learners a realistic introduction to underground
                                            mining before they arrive on site. It builds familiarity with the
                                            environment, language, equipment, processes, and safety expectations they
                                            will encounter during hands-on training.
                                        </p>
                                        <br />
                                        <p>
                                            By connecting the full mining cycle with the people and careers behind it,
                                            the program provides a strong foundation for deeper learning as future
                                            modules are added.
                                        </p>
                                    </div>
                                </div>

                                <div className="col-lg-5 offset-lg-1 second-column">
                                    <div className="content-wrapper">
                                        <h4 className="heading heading-small light-2">PLATFORM</h4>
                                        <div className="paragraph light-1">
                                            <p>PCVR, NXR</p>
                                        </div>
                                    </div>

                                    <div className="content-wrapper">
                                        <h4 className="heading heading-small light-2">CLIENT</h4>
                                        <div className="paragraph light-1">
                                            <p>NORCAT</p>
                                        </div>
                                    </div>

                                    <div className="content-wrapper eo-acknowledgement">
                                        <picture>
                                            <source media="(max-width: 1024px)" srcSet={eoLogoLow} />
                                            <img
                                                className="eo-logo-primary"
                                                src={eoLogo}
                                                alt="This Employment Ontario initiative is funded in part by the Government of Canada and the Government of Ontario."
                                                loading="lazy"
                                                decoding="async"
                                            />
                                        </picture>
                                        <p>
                                            This{' '}
                                            <a
                                                href="https://www.ontario.ca/page/employment-ontario"
                                                target="_blank"
                                                rel="noreferrer"
                                            >
                                                Employment Ontario
                                            </a>{' '}
                                            initiative is funded in part by the{' '}
                                            <a href="https://www.canada.ca/en.html" target="_blank" rel="noreferrer">
                                                Government of Canada
                                            </a>{' '}
                                            and the{' '}
                                            <a
                                                href="https://www.ontario.ca/page/government-ontario"
                                                target="_blank"
                                                rel="noreferrer"
                                            >
                                                Government of Ontario
                                            </a>
                                            .
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="text-content-footer">
                        <div className="container">
                            <div className="row align-items-lg-center">
                                <div className="col-md-6">
                                    <h3 className="heading heading-large light-1">
                                        Have an idea?
                                        <br />
                                        Let's explore it together.
                                    </h3>
                                </div>
                                <div className="col-md-6 d-md-inline-flex justify-content-md-end">
                                    <div className="button-wrapper">
                                        <Link className="button button-1" to="/Contact">
                                            Contact Us
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="artwork artwork-2" aria-hidden="true">
                        <img src={artwork} alt="" loading="lazy" decoding="async" />
                    </div>
                </div>
            </div>
        </>
    ),
};

export default Data_NORCATUndergroundExperience;
